
/**
 * Competence Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useCompetenceStore } from '../../store/competence.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { Logger } from '@/lib/app-log.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class CompetenceIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchCompetences(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useCompetenceStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setCompetences(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                store.setPaginationData({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async createCompetence(data) {
        const store = useCompetenceStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('iso27001Competence:messages.createSuccess', { defaultValue: 'Competence created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async updateCompetence(id, data) {
        const store = useCompetenceStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('iso27001Competence:messages.updateSuccess', { defaultValue: 'Competence updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteCompetence(id) {
        const store = useCompetenceStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            await this.service.delete(id);
            toast.success(i18next.t('iso27001Competence:messages.deleteSuccess', { defaultValue: 'Competence deleted' }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async batchDeleteCompetence(ids) {
        const store = useCompetenceStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);
            toast.success(i18next.t('iso27001Competence:messages.bulkDeleteSuccess', { count: ids.length, defaultValue: `${ids.length} items deleted` }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchPersonnel(id) {
        try {
            const response = await this.service.getPersonnel(id);
            // response from SDK: { success, data: { success, data: [...] } } or { success, data: [...] }
            const personnel = response?.data?.data || response?.data || [];
            return { success: true, data: Array.isArray(personnel) ? personnel : [] };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            Logger.error('fetchPersonnel error:', { error: errorMessage });
            toast.error(errorMessage);
            return { success: false, error: errorMessage, data: [] };
        }
    }


    async assignPersonnel(id, userIds) {
        try {
            const response = await this.service.assignPersonnel(id, userIds);
            toast.success(
                i18next.t('iso27001Competence:messages.assignSuccess', {
                    defaultValue: 'Personnel assigned',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async unassignPersonnel(id, userId) {
        try {
            const response = await this.service.unassignPersonnel(id, userId);
            toast.success(
                i18next.t('iso27001Competence:messages.unassignSuccess', {
                    defaultValue: 'Personnel unassigned',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    async listByUser(userId) {
        try {
            const response = await this.service.listByUser(userId);
            const rows = response?.data?.data || response?.data || [];
            return { success: true, data: Array.isArray(rows) ? rows : [] };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage, data: [] };
        }
    }

    async linkForUser(userId, competenceIds) {
        try {
            const response = await this.service.linkForUser(userId, competenceIds);
            toast.success(
                i18next.t('iso27001Competence:messages.linkSuccess', {
                    defaultValue: 'Competences linked',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundKey: 'iso27001Competence:messages.notFound',
            notFoundDefault: 'Competence not found',
        });
    }
}
